import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { IMAGES, bgStyle } from '@/config/images';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Bot, ChevronLeft, Send, Printer, Wrench, Layers } from 'lucide-react';

const Print3DAskAI = () => {
  const [params] = useSearchParams();
  const printerId = params.get('printer_id') || '';
  const slicer = params.get('slicer') || '';
  const token = localStorage.getItem('token');

  const [printers, setPrinters] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const endRef = useRef(null);

  // Determine context type: printer or slicer
  const contextType = printerId ? 'printer' : slicer ? 'slicer' : 'general';

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const load = async () => {
      if (!token) return;
      try {
        const res = await axios.get(`${API}/printing3d/printers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPrinters(res.data || []);
      } catch (e) {
        console.error('Failed to load printers for troubleshooting chat', e);
      }
    };
    load();
  }, [token]);

  const printer = useMemo(
    () => (printerId ? printers.find((p) => p.id === printerId) || null : null),
    [printers, printerId]
  );

  const intro = useMemo(() => {
    if (printer) {
      return `Troubleshooting context loaded for your printer: ${printer.name || 'Printer'}${printer.brand ? ` (${printer.brand} ${printer.model || ''})` : ''}. Describe your print issue - what symptoms are you seeing?`;
    }
    if (slicer) {
      return `Slicer support loaded for: ${slicer}. What issue are you having with your slicer settings, profiles, or G-code output?`;
    }
    return 'Select a printer or slicer from the Troubleshooting page to get personalized help, or describe your issue below.';
  }, [printer, slicer]);

  useEffect(() => {
    setMessages([
      {
        role: 'assistant',
        content: intro,
      },
    ]);
    setSessionId(null);
  }, [intro]);

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim() || !token) return;

    const text = input;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setIsLoading(true);

    try {
      const payload = {
        message: text,
        session_id: sessionId,
        printer_id: printerId || undefined,
        slicer: slicer || undefined,
      };

      const res = await axios.post(`${API}/printing3d/ai/chat`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages((prev) => [...prev, { role: 'assistant', content: res.data.response }]);
      setSessionId(res.data.session_id);
    } catch (err) {
      toast.error('Failed to get AI response');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" data-testid="print3d-ask-ai" style={bgStyle(IMAGES.AI_ASSISTANT)}>
      <div className="fixed inset-0 bg-black/90 -z-10" style={{ pointerEvents: 'none' }} />
      {/* Header */}
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/3d-printing/troubleshooting" className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
            <ChevronLeft className="w-5 h-5" />
            Back
          </Link>
        </div>

        <div className="mb-6">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-lg ${contextType === 'slicer' ? 'bg-blue-500/20' : 'bg-orange-500/20'}`}>
              <Bot className={`w-8 h-8 ${contextType === 'slicer' ? 'text-blue-500' : 'text-orange-500'}`} />
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-orange-500 mb-1">AI ASSISTANT</div>
              <h1 
                className="text-3xl md:text-4xl font-bold text-orange-500 tracking-tight uppercase"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                {contextType === 'slicer' ? 'Ask AI - Slicer Expert' : 'Ask AI - Print Expert'}
              </h1>
              <p className="text-sm md:text-base text-zinc-300 mt-1">
                {printer 
                  ? `Getting help for: ${printer.name}` 
                  : slicer
                  ? `Getting help with: ${slicer}`
                  : 'Get AI-powered troubleshooting help'
                }
              </p>
            </div>
          </div>
          
          {printer && (
            <div className="mt-4 p-3 bg-black/70 rounded-lg border border-orange-500/30 flex items-center gap-3">
              <Printer className="w-5 h-5 text-orange-400" />
              <div>
                <div className="text-white font-medium">{printer.name}</div>
                <div className="text-xs text-zinc-400">
                  {printer.brand} {printer.model} • {printer.nozzle_diameter}mm nozzle • {printer.direct_drive ? 'Direct Drive' : 'Bowden'}
                </div>
              </div>
            </div>
          )}

          {slicer && (
            <div className="mt-4 p-3 bg-zinc-800 rounded-lg border border-blue-500/30 flex items-center gap-3">
              <Layers className="w-5 h-5 text-blue-400" />
              <div>
                <div className="text-white font-medium">{slicer}</div>
                <div className="text-xs text-zinc-500">
                  Slicer software support
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Chat Panel */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3 text-sm text-zinc-400">
            <Wrench className={`w-4 h-4 ${contextType === 'slicer' ? 'text-blue-400' : 'text-orange-400'}`} />
            <span>
              {contextType === 'slicer'
                ? 'Tip: Describe your slicer version, the specific setting you\'re struggling with, and what result you\'re trying to achieve.'
                : 'Tip: Include your material, layer height, temperatures, and describe what the failed print looks like.'
              }
            </span>
          </div>

          <ScrollArea className="h-[460px] w-full rounded-lg border border-zinc-700 bg-[#1f1f1f] p-4">
            <div className="space-y-3">
              {messages.map((m, idx) => (
                <div
                  key={idx}
                  className={
                    m.role === 'user'
                      ? 'flex justify-end'
                      : 'flex justify-start'
                  }
                >
                  <div
                    className={
                      m.role === 'user'
                        ? 'max-w-[85%] rounded-lg bg-orange-500/15 border border-orange-500/30 px-3 py-2 text-sm text-white'
                        : 'max-w-[85%] rounded-lg bg-zinc-800 border border-zinc-700 px-3 py-2 text-sm'
                    }
                  >
                    {m.role === 'assistant' && (
                      <div className="flex items-center gap-2 mb-1 text-xs text-orange-500">
                        <Bot className="w-3.5 h-3.5" />
                        AI Assistant
                      </div>
                    )}
                    <div className={`whitespace-pre-wrap leading-relaxed ${m.role === 'assistant' ? 'text-zinc-300' : ''}`}>{m.content}</div>
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="text-xs text-orange-500 animate-pulse">Thinking…</div>
              )}
              <div ref={endRef} />
            </div>
          </ScrollArea>

          <form onSubmit={send} className="mt-3 flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Describe your print problem... (e.g., 'My prints have stringing between parts')"
              className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
              disabled={isLoading || !token}
            />
            <Button
              type="submit"
              disabled={isLoading || !input.trim() || !token}
              className="bg-orange-600 hover:bg-orange-500"
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Print3DAskAI;
